import { useEffect } from 'react'

import { type BrokersTaskDetail } from 'utils/web_api.schemas'

import { DrawerProvider, useDrawer } from './Drawer'
import Notes from './Notes'
import { useCreateNote } from '../hooks/useCreateNote'
import { getNotesQueryKey, useGetNotes } from '../hooks/useGetNotes'

/**
 * TaskNotes component for displaying and creating notes associated with a specific task.
 *
 * This component fetches notes where subject_type="Task" and subject_id=task.id,
 * and allows creating new notes with the task as the subject.
 *
 * Unlike MemberNotes, this component does NOT wrap itself in a QueryClientProvider
 * since it's designed to be used within TasksView which already provides one.
 *
 * Usage:
 * ```tsx
 * <TaskNotes task={selectedTask} />
 *
 * // Or as a drawer
 * <TaskNotesDrawer task={selectedTask} />
 * ```
 */
type TaskNotesProps = {
  task: BrokersTaskDetail
  displayExistingNotes?: boolean
}

const TaskNotesImpl = ({
  task,
  displayExistingNotes = true,
}: TaskNotesProps) => {
  const queryKey = getNotesQueryKey({
    subjectType: 'Tasks::Task',
    subjectId: task.id,
  })

  const {
    data: notes,
    error,
    isLoading,
  } = useGetNotes({
    subjectType: 'Tasks::Task',
    subjectId: task.id,
    displayExistingNotes,
  })

  const createNoteMutation = useCreateNote({
    subjectType: 'Tasks::Task',
    subjectId: task.id,
    // Include memberId if the task has an associated member for proper association
    ...(task.member_id ? { memberId: task.member_id } : {}),
  })

  const { setIsExpanded } = useDrawer()

  // Expand the drawer if there are 3 or more notes.
  useEffect(() => {
    if (notes && notes.length >= 3) {
      setIsExpanded(true)
    }
  }, [notes, setIsExpanded])

  return (
    <Notes
      isLoading={isLoading}
      error={error}
      createNoteMutation={createNoteMutation}
      displayExistingNotes={displayExistingNotes}
      notes={notes || []}
      queryKey={queryKey}
    />
  )
}

export const TaskNotesDrawer = (props: TaskNotesProps) => {
  return (
    <DrawerProvider>
      <div
        className='h-full flex flex-col overflow-hidden'
        data-drawer='task-notes'
      >
        {/* Header - fixed at top */}
        <div className='flex-shrink-0 p-4 flex items-center justify-between'>
          <h2 className='h2 m-0'>Task Notes</h2>
        </div>

        {/* Scrollable content area */}
        <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400'>
          <TaskNotesImpl {...props} />
        </div>
      </div>
    </DrawerProvider>
  )
}

const TaskNotes = (props: TaskNotesProps) => {
  return <TaskNotesImpl {...props} />
}

export default TaskNotes
